import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import get from 'lodash/get';
import { PorletHeaderTitle } from 'components/porletHeader';
import { getFasaraViewSheetImgSrc } from 'preview/sheetImageSrc';
import {
  fetchFasaraFilmsForFamily,
  fetchFasaraFamiliesForCategory,
  isPlaceholderFasaraFilmData,
  pickFamilyLabelFromFamiliesList,
  pickProductLabelFromFilmsList,
} from '../../fasaraCatalogApi';

function pickFirstNonempty(...parts) {
  for (let i = 0; i < parts.length; i += 1) {
    const p = parts[i];
    if (p != null && String(p).trim() !== '') {
      return String(p).trim();
    }
  }
  return '';
}

function FasaraSheetRow({ sheet, index, total }) {
  const [labels, setLabels] = useState({
    product: '',
    family: '',
  });

  useEffect(() => {
    let cancelled = false;
    const fd = sheet.filmData;

    if (!isPlaceholderFasaraFilmData(fd)) {
      setLabels({
        product: pickFirstNonempty(fd?.name, sheet.film),
        family: pickFirstNonempty(
          fd?.groupData?.name,
          fd?.groupData?.category,
          sheet.family,
        ),
      });
      return undefined;
    }

    const familyId = sheet.family;
    const filmId = sheet.film;
    const category = sheet.glassFinish;

    if (!familyId || filmId == null || filmId === '') {
      setLabels({
        product: String(filmId ?? ''),
        family: String(familyId ?? ''),
      });
      return undefined;
    }

    (async () => {
      try {
        const films = await fetchFasaraFilmsForFamily(familyId);
        if (cancelled) return;
        const productLabel =
          pickProductLabelFromFilmsList(films, filmId) ?? String(filmId);

        let familyLabel = String(familyId);
        if (category) {
          const families = await fetchFasaraFamiliesForCategory(category);
          if (cancelled) return;
          familyLabel =
            pickFamilyLabelFromFamiliesList(families, familyId) ??
            String(familyId);
        }

        setLabels({ product: productLabel, family: familyLabel });
      } catch {
        if (!cancelled) {
          setLabels({ product: String(filmId), family: String(familyId) });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sheet.id, sheet.film, sheet.family, sheet.glassFinish, sheet.filmData]);

  const fd = sheet.filmData;
  const placeholder = isPlaceholderFasaraFilmData(fd);

  const categoryDisplay = placeholder
    ? pickFirstNonempty(sheet.glassFinish, fd?.groupData?.category)
    : pickFirstNonempty(
        fd?.groupData?.category,
        sheet.glassFinish,
        fd?.groupData?.name,
      );

  const familyDisplay = placeholder
    ? labels.family || pickFirstNonempty(sheet.family, fd?.groupData?.name)
    : pickFirstNonempty(fd?.groupData?.name, labels.family, sheet.family);

  const productDisplay = placeholder
    ? labels.product || pickFirstNonempty(sheet.film, fd?.name)
    : pickFirstNonempty(fd?.name, labels.product, sheet.film);

  const sheetForImg = placeholder ? { ...sheet, filmData: null } : sheet;

  return (
    <div className="row">
      <div className="col-12 ViewOrEdit">
        <div>
          <strong>
            <FormattedMessage id="Categoría" />
          </strong>
          <span className="w-50">{categoryDisplay}</span>
        </div>
        <div className="kt-space-20" />
        <div>
          <strong>
            <FormattedMessage id="Familia de producto" />
          </strong>
          <span className="w-50">{familyDisplay}</span>
        </div>
        <div className="kt-space-20" />
        <div>
          <strong>
            <FormattedMessage id="Producto" />
          </strong>
          <span className="w-50">{productDisplay}</span>
        </div>
        <div className="kt-space-20" />
        <div>
          <strong>
            <FormattedMessage id="M2 Necesarios:" />
          </strong>
          <span className="w-50">{sheet.meters}</span>
        </div>
        <div className="kt-space-20" />
        <div>
          <strong>
            <FormattedMessage id="Distribuidor:" />
          </strong>
          <span className="w-50">{sheet.distributor?.name ?? ''}</span>
        </div>
        <div className="kt-space-20" />
        <div>
          <strong>
            <FormattedMessage id="Número de RUN:" />
          </strong>
          <span className="w-50">{sheet.run}</span>
        </div>
        <div className="kt-space-20" />
        <div>
          <strong>
            <FormattedMessage id="Vista Previa" />
          </strong>
          <div className="w-50">
            <img
              src={getFasaraViewSheetImgSrc(sheetForImg)}
              alt={productDisplay}
              className="img-previewer-summary w-100"
            />
          </div>
        </div>
        {total - 1 !== index && <div className="kt-space-20" />}
      </div>
    </div>
  );
}

export const Sheets = ({ values }) => {
  const applications = get(values, 'data.applications', []) || [];

  return (
    <div>
      {Boolean(applications.length) && (
        <>
          <PorletHeaderTitle variant="h5">
            <FormattedMessage id="3M™ Glass Finishes" />
          </PorletHeaderTitle>
          <div className="kt-space-20" />
          {applications.map((sheet, index) => (
            <FasaraSheetRow
              key={sheet.id ?? index}
              sheet={sheet}
              index={index}
              total={applications.length}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default Sheets;
